import type { ComponentType } from 'react'

import _ from 'lodash'

import {
  ConfirmationForm,
  ForgotPasswordForm,
  ResendConfirmationForm,
  ResetPasswordForm,
  SignUpForm
} from '@/features/auth'
import { AuthBrandingCompact } from '@/features/auth/layout/AuthBrandingCompact'
import { AuthPageHeader } from '@/features/auth/layout/AuthPageHeader'
import { AuthPageLayout } from '@/features/auth/layout/AuthPageLayout'
import { useAuthContext } from '@/features/auth/model/auth-context'
import { withAuth } from '@/features/auth/model/auth-context'
import getConfig from '@/features/auth/model/config'
import { useDigitalProduct } from '@/shared/hooks/useDigitalProduct'

type AuthSubRouteAction =
  | 'sign-up'
  | 'forgot-password'
  | 'reset-password'
  | 'confirmation'
  | 'resend-confirmation'

const FORM_BY_ACTION: Record<AuthSubRouteAction, ComponentType> = {
  'sign-up': SignUpForm,
  'forgot-password': ForgotPasswordForm,
  'reset-password': ResetPasswordForm,
  confirmation: ConfirmationForm,
  'resend-confirmation': ResendConfirmationForm
}

/** Props que a rota fornece. A ação é fixada pela fábrica, não pelo consumidor. */
type AuthSubRoutePageProps = {
  digitalProductId?: string
}

/** Chave do `config` que guarda o cabeçalho de cada ação. */
const CONFIG_KEY_BY_ACTION: Record<AuthSubRouteAction, string> = {
  'sign-up': 'signUp',
  'forgot-password': 'forgotPassword',
  'reset-password': 'resetPassword',
  confirmation: 'confirmation',
  'resend-confirmation': 'resendConfirmation'
}

function AuthSubRoutePageContent({
  action
}: AuthSubRoutePageProps & { action: AuthSubRouteAction }) {
  const { digitalProduct } = useDigitalProduct()
  const { config } = useAuthContext()
  const Form = FORM_BY_ACTION[action]
  const pageAction = action === 'sign-up' ? 'sign-up' : 'sub-route'

  // Título e subtítulo vêm do mesmo lugar que o acesso usa — o `config` —, em
  // vez de cada formulário trazer o próprio cabeçalho.
  const header = _.get(config, `${CONFIG_KEY_BY_ACTION[action]}.header`) as
    | { title: string; subtitle?: string }
    | undefined

  return (
    <AuthPageLayout
      digitalProduct={digitalProduct}
      pageAction={pageAction}
      showHero={false}
      showValueProps={false}
    >
      <AuthBrandingCompact digitalProduct={digitalProduct} />
      {header?.title ? (
        <AuthPageHeader title={header.title} subtitle={header.subtitle} />
      ) : null}
      <Form />
    </AuthPageLayout>
  )
}

function createAuthSubRoutePage(action: AuthSubRouteAction) {
  const Page = (props: AuthSubRoutePageProps) => (
    <AuthSubRoutePageContent {...props} action={action} />
  )

  return withAuth(Page, () => getConfig())
}

export const AuthSignUpPage = createAuthSubRoutePage('sign-up')
export const AuthForgotPasswordPage = createAuthSubRoutePage('forgot-password')
export const AuthResetPasswordPage = createAuthSubRoutePage('reset-password')
export const AuthConfirmationPage = createAuthSubRoutePage('confirmation')
export const AuthResendConfirmationPage = createAuthSubRoutePage(
  'resend-confirmation'
)
